import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import { Attachment } from '@shared/models';
import {
  ArchiveSetDeleting,
  AttachmentsDeleteMultiple,
  AttachmentsDeleteMultipleSuccess,
  AttachmentsCreateMultiple,
  AttachmentsCreateMultipleSuccess,
} from 'mojo-irp-attachments';

import {
  attachmentsClear,
  attachmentsCreateSuccess,
  attachmentsDeleteSuccess,
  attachmentsGetLatestCoverLetterFailure,
  attachmentsGetLatestCoverLetterSuccess,
  attachmentsGetLatestResumeFailure,
  attachmentsGetLatestResumeSuccess,
  attachmentsGetResumesSuccess,
  attachmentsGetSuccess,
  attachmentsLoadSuccess,
} from './attachments.actions';
import { createReducer, on } from '@ngrx/store';

export interface AttachmentsState extends EntityState<Attachment> {
  resumesLoaded: boolean;
  coverLettersLoaded: boolean;
  othersLoaded: boolean;
  noteAttachment: { loaded: boolean; noteId: number; isDeleting: boolean };
}

export const attachmentsAdapter: EntityAdapter<Attachment> = createEntityAdapter<Attachment>({});

export const initialState: AttachmentsState = attachmentsAdapter.getInitialState({
  resumesLoaded: false,
  coverLettersLoaded: false,
  othersLoaded: false,
  noteAttachment: { loaded: true, noteId: 0, isDeleting: false },
});

export const attachmentsReducer = createReducer(
  initialState,
  on(attachmentsClear, (state) =>
    attachmentsAdapter.removeAll({
      ...state,
      resumesLoaded: false,
      coverLettersLoaded: false,
      othersLoaded: false,
    }),
  ),
  on(attachmentsCreateSuccess, (state, { payload }) => attachmentsAdapter.addOne(payload, state)),
  on(attachmentsGetLatestResumeSuccess, (state, { payload }) =>
    attachmentsAdapter.upsertOne(payload, {
      ...state,
      resumesLoaded: true,
    }),
  ),
  on(attachmentsGetLatestCoverLetterSuccess, (state, { payload }) =>
    attachmentsAdapter.upsertOne(payload, {
      ...state,
      coverLettersLoaded: true,
    }),
  ),
  on(attachmentsGetSuccess, (state, { attachment }) =>
    attachmentsAdapter.upsertOne(attachment, state),
  ),
  on(attachmentsGetResumesSuccess, (state, { payload }) =>
    attachmentsAdapter.upsertMany(payload, {
      ...state,
      resumesLoaded: true,
    }),
  ),
  on(attachmentsGetLatestResumeFailure, (state) => ({ ...state, resumesLoaded: true })),
  on(attachmentsGetLatestCoverLetterFailure, (state) => ({ ...state, coverLettersLoaded: true })),
  on(attachmentsLoadSuccess, (state, { payload }) =>
    attachmentsAdapter.upsertMany(payload, {
      ...state,
      othersLoaded: true,
    }),
  ),
  on(attachmentsDeleteSuccess, (state, { payload }) =>
    attachmentsAdapter.removeOne(payload, state),
  ),
  on(ArchiveSetDeleting, (state) => ({
    ...state,
    noteAttachment: { loaded: false, noteId: state.noteAttachment.noteId, isDeleting: true },
  })),
  on(AttachmentsDeleteMultiple, (state, { associateModuleId }) => ({
    ...state,
    noteAttachment: { loaded: false, noteId: associateModuleId, isDeleting: true },
  })),
  on(AttachmentsDeleteMultipleSuccess, (state, { associateModuleId }) => ({
    ...state,
    noteAttachment: { loaded: true, noteId: associateModuleId, isDeleting: false },
  })),
  on(AttachmentsCreateMultiple, (state, { params }) => ({
    ...state,
    noteAttachment: {
      loaded: false,
      noteId: params.associateModuleId,
      isDeleting: false,
    },
  })),
  on(AttachmentsCreateMultipleSuccess, (state, { payload }) => ({
    ...state,
    noteAttachment: {
      loaded: true,
      noteId: !!payload[0] ? payload[0].associateModuleId : 0,
      isDeleting: false,
    },
  })),
);
